import React from "react";
import { Chat } from "../../types/conversations";
import { ChatSearch } from "./ChatSearch";
import { ChatListItem } from "./ChatListItem";
import Skeleton from "../ui/Skeleton";

interface ChatListProps {
  chats: Chat[];
  selectedChatId: number | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onChatSelect: (chatId: number | null) => void;
  loading?: boolean;
  onRefresh?: () => void;
  onScroll?: () => void;
  chatsContainerRef?: React.RefObject<HTMLDivElement>;
  isLoadingMore?: boolean;
  /** Se true, o usuário logado é profissional (badge "Aluno"); se false, é aluno (badge "Meu professor") */
  viewerIsProfessional?: boolean;
}

const ChatListSkeleton: React.FC = () => {
  return (
    <div className="flex items-center gap-3 px-3 py-3 border-b border-gray-100 dark:border-gray-700 md:px-4">
      <Skeleton className="h-11 w-11 rounded-full md:h-12 md:w-12" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <Skeleton className="h-4 w-24 md:w-32" />
          <Skeleton className="h-3 w-10 md:w-12" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-32 md:w-48" />
          <Skeleton className="h-5 w-5 rounded-full" />
        </div>
      </div>
    </div>
  );
};

export const ChatList: React.FC<ChatListProps> = ({
  chats,
  selectedChatId,
  searchQuery,
  onSearchChange,
  onChatSelect,
  loading = false,
  onRefresh,
  onScroll,
  chatsContainerRef,
  isLoadingMore = false,
  viewerIsProfessional = false,
}) => {
  return (
    <div className="w-full bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex flex-col h-full md:w-96 md:border-b-0 md:border-r">
      <ChatSearch
        value={searchQuery}
        onChange={onSearchChange}
        onRefresh={onRefresh}
        onBack={() => onChatSelect(null)}
        hasSelectedChat={!!selectedChatId}
      />

      <div
        ref={chatsContainerRef}
        className="conversations-scroll flex-1 overflow-y-auto"
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
        onScroll={onScroll}
      >
        {loading ? (
          <div>
            {[...Array(8)].map((_, i) => (
              <ChatListSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {chats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChatId === chat.id}
                onClick={() => onChatSelect(chat.id)}
                viewerIsProfessional={viewerIsProfessional}
              />
            ))}
            {isLoadingMore && (
              <div>
                {[...Array(3)].map((_, i) => (
                  <ChatListSkeleton key={`loading-${i}`} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
